import Button from '@orn/shared-web/components/Button/Button';
import { OTPInput } from '@orn/shared-web/components/Input';

import { useCurrentUser } from '../../../hooks/queries/useCurrentUser';
import useFormattedPhone from '../../../hooks/useFormattedPhone';
import usePhoneVerification from '../../../hooks/usePhoneVerification';
import WizardContainer from '../WizardContainer';
import WizardTitle from '../WizardTitle';

import type { IWizardStepProps } from './types';

const VerifyPhoneStep = ({ onNext, onBack }: IWizardStepProps) => {
  const { user } = useCurrentUser();
  const {
    code,
    setCode,
    error,
    codeSent,
    isSending,
    isVerifying,
    secondsRemaining,
    isCountdownActive,
    handleSendCode,
    handleVerify,
  } = usePhoneVerification(onNext);

  const rawPhone = user?.phoneNumber ?? '';
  const phoneNumber = useFormattedPhone(rawPhone);

  return (
    <WizardContainer step={2}>
      <div className="flex flex-col gap-2">
        <WizardTitle title="Verify your phone" />

        <div className="flex flex-col gap-4 pb-8 pt-0">
          {!codeSent ? (
            <>
              <div className="text-secondary text-sm">
                We'll send a verification code to <span className="text-primary font-medium">{phoneNumber}</span> to
                confirm your number.
              </div>
              <Button color="primary" size="xl" className="w-full" onClick={handleSendCode} isLoading={isSending}>
                Send code
              </Button>
            </>
          ) : (
            <>
              <div className="text-secondary text-center text-sm">
                Enter the code sent to <span className="text-primary font-medium">{phoneNumber}</span>.
              </div>
              <div className="flex justify-center">
                <OTPInput
                  value={code}
                  onChange={setCode}
                  onComplete={(c) => void handleVerify(c)}
                  isInvalid={!!error}
                  errorMessage={error || undefined}
                  isDisabled={isVerifying}
                  autoFocus
                />
              </div>
              <Button
                color="primary"
                size="xl"
                className="w-full"
                onClick={() => void handleVerify(code)}
                isLoading={isVerifying}
                isDisabled={code.length !== 6}
              >
                Verify
              </Button>
              <div className="flex justify-center">
                {isCountdownActive ? (
                  <span className="text-tertiary text-sm">Resend code in {secondsRemaining}s</span>
                ) : (
                  <Button color="link-color" size="sm" onClick={handleSendCode} isDisabled={isSending}>
                    Resend code
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-2">
        <div className="flex gap-2">
          {onBack && (
            <Button color="secondary" size="xl" className="flex-1" onClick={onBack}>
              Back
            </Button>
          )}
          <Button color="secondary" size="xl" className="flex-1" onClick={onNext}>
            Skip for now
          </Button>
        </div>
      </div>
    </WizardContainer>
  );
};

export default VerifyPhoneStep;
